import { notificationApi } from "../config/apiConfig";
import { getActorEmail } from "../storage/AuthStorage";
import {
  PanelState,
  PreferenceUpdate,
  SettingChange,
  StationSubscription,
  UserNotificationPreferences,
} from "../types/notificationSettings";

/**
 * Turns any failure into the state a panel should show. This module is the only
 * place that decides it, so no panel reimplements the 403 rule.
 */
export const stateForError = (error: any): PanelState =>
  error?.response?.status === 403 ? "refused" : "failed";

export const messageForError = (error: any): string =>
  error?.response?.data?.message ?? error?.message ?? "Request failed";

/**
 * Names the member of staff on every write. The service prefers the address on the token and only
 * falls back to this, so the header is a label — the token subject stays the identifier.
 */
const actorHeaders = () => {
  const email = getActorEmail();
  return email ? { headers: { "X-Actor-Email": email } } : {};
};

const toCategory = (raw: any) => ({
  channels: raw?.channels ?? {},
  snoozedUntil: raw?.snoozed_until ?? null,
});

const toPreferences = (raw: any): UserNotificationPreferences => ({
  userId: raw?.user_id ?? "",
  email: raw?.email ?? null,
  ownerAlerts: toCategory(raw?.preferences?.owner_alerts),
  subscribed: toCategory(raw?.preferences?.subscribed),
});

const toSubscription = (raw: any): StationSubscription => ({
  stationId: raw?.station_id ?? "",
  evses: raw?.evses ?? [],
  statuses: raw?.statuses ?? [],
});

const toChange = (raw: any): SettingChange => ({
  changeId: raw?.change_id ?? "",
  changedAt: raw?.changed_at ?? "",
  actor: raw?.actor ?? "",
  actorEmail: raw?.actor_email ?? null,
  actorKind: raw?.actor_kind ?? "",
  before: raw?.before ?? {},
  after: raw?.after ?? {},
});

/** Reads the effective preferences: a person with nothing stored is on the defaults, not empty. */
export const getPreferences = async (userId: string): Promise<UserNotificationPreferences> => {
  const response = await notificationApi.get(
    `/notification-preferences/owners/${encodeURIComponent(userId)}`,
  );
  return toPreferences(response.data);
};

/** Sends only what changed; returns what the service now holds, never what was typed. */
export const updatePreferences = async (
  userId: string,
  update: PreferenceUpdate,
): Promise<UserNotificationPreferences> => {
  const category: Record<string, unknown> = {};
  if (update.channels) category.channels = update.channels;
  if (update.clearSnooze) category.snoozed_until = null;
  else if (update.snoozedUntil) category.snoozed_until = update.snoozedUntil;

  const response = await notificationApi.put(
    `/notification-preferences/owners/${encodeURIComponent(userId)}`,
    { [update.category]: category },
    actorHeaders(),
  );
  return toPreferences(response.data);
};

export const getSubscriptions = async (userId: string): Promise<StationSubscription[]> => {
  const response = await notificationApi.get(
    `/subscriptions/users/${encodeURIComponent(userId)}/station-status`,
  );
  return (response.data ?? []).map(toSubscription);
};

/**
 * Declarative, and scoped to the chargers sent: naming chargers replaces the statuses held for
 * those chargers only, while naming none is about the whole station. An empty status list therefore
 * removes exactly what the same chargers cover.
 */
export const setSubscription = async (
  userId: string,
  stationId: string,
  evses: string[],
  statuses: string[],
): Promise<StationSubscription[]> => {
  const response = await notificationApi.post(
    `/subscriptions/users/${encodeURIComponent(userId)}/station-status`,
    { station_id: stationId, evses, statuses },
    actorHeaders(),
  );
  return (response.data ?? []).map(toSubscription);
};

/** Every charger of a station at once, whatever the person's rows are scoped to. */
export const deleteSubscription = async (userId: string, stationId: string): Promise<void> => {
  await notificationApi.delete(
    `/subscriptions/users/${encodeURIComponent(userId)}/station-status/${encodeURIComponent(stationId)}`,
    actorHeaders(),
  );
};

export interface StationSubscriber {
  userId: string;
  evses: string[];
  statuses: string[];
}

/** Everyone watching one station. The station is the table's partition key, so this is cheap. */
export const getStationSubscribers = async (stationId: string): Promise<StationSubscriber[]> => {
  const response = await notificationApi.get(
    `/subscriptions/stations/${encodeURIComponent(stationId)}`,
  );
  return (response.data ?? []).map((raw: any) => ({
    userId: raw?.user_id ?? "",
    evses: raw?.evses ?? [],
    statuses: raw?.statuses ?? [],
  }));
};

export const getSettingHistory = async (
  userId: string,
  limit = 50,
): Promise<SettingChange[]> => {
  const response = await notificationApi.get(
    `/notification-preferences/owners/${encodeURIComponent(userId)}/audit`,
    { params: { limit } },
  );
  return (response.data ?? []).map(toChange);
};
